import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { User, Phone, Mail, Lock, UserPlus, Eye, EyeOff, CheckCircle2, AtSign } from 'lucide-react'
import { AuthCard } from '../components/AuthCard'
import { SocialButtons } from '../components/SocialButtons'
import { useRegisterMutation } from '@/modules/auth-api'
import { z } from 'zod'

const PHONE_REGEX = /^(0|\+84)(3[2-9]|5[25689]|7[06-9]|8[1-9]|9[0-9])\d{7}$/

const registerSchema = z.object({
  fullName: z.string().trim().min(2, 'Họ tên tối thiểu 2 ký tự').max(80),
  username: z.string().trim().min(3, 'Username tối thiểu 3 ký tự').max(40).regex(/^[a-z0-9_.]+$/i, 'Chỉ chữ/số/dấu . _'),
  phone:    z.string().trim().regex(PHONE_REGEX, 'Số điện thoại không hợp lệ'),
  email:    z.string().trim().email('Email không hợp lệ'),
  password: z.string().min(6, 'Mật khẩu tối thiểu 6 ký tự'),
  confirm:  z.string().min(6, 'Vui lòng nhập lại mật khẩu'),
  agree:    z.literal(true, { errorMap: () => ({ message: 'Bạn cần đồng ý điều khoản' }) }),
}).refine((d) => d.password === d.confirm, {
  path: ['confirm'],
  message: 'Mật khẩu nhập lại không khớp',
})
type RegisterValues = z.infer<typeof registerSchema>
type Errors = Partial<Record<keyof RegisterValues, string>>

export function RegisterPage() {
  const navigate = useNavigate()
  const registerMut = useRegisterMutation()

  const [values, setValues] = useState<RegisterValues>({
    fullName: '', username: '', phone: '', email: '', password: '', confirm: '', agree: false as unknown as true,
  })
  const [errors, setErrors] = useState<Errors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [showPass, setShowPass] = useState(false)
  const [success, setSuccess] = useState(false)

  const upd = <K extends keyof RegisterValues>(k: K, v: RegisterValues[K]) => {
    setValues((s) => ({ ...s, [k]: v }))
    setErrors((e) => ({ ...e, [k]: undefined }))
    setServerError(null)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setServerError(null)
    const parsed = registerSchema.safeParse(values)
    if (!parsed.success) {
      const next: Errors = {}
      for (const i of parsed.error.issues) {
        const k = i.path[0] as keyof RegisterValues
        if (!next[k]) next[k] = i.message
      }
      setErrors(next)
      return
    }
    try {
      await registerMut.mutateAsync({
        fullName: values.fullName,
        username: values.username,
        phone: values.phone,
        email: values.email,
        password: values.password,
      })
      setSuccess(true)
      setTimeout(() => navigate('/', { replace: true }), 1200)
    } catch (err: any) {
      setServerError(err?.message ?? 'Đăng ký thất bại')
    }
  }

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface pl-10 pr-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const strength = (() => {
    const p = values.password
    let s = 0
    if (p.length >= 6) s++
    if (p.length >= 10) s++
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++
    if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) s++
    return s
  })()
  const strengthLabel = ['Yếu', 'Yếu', 'Trung bình', 'Khá', 'Mạnh'][strength]
  const strengthColor = ['bg-red-400', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-accent-500'][strength]

  return (
    <AuthCard
      title="Tạo tài khoản mới"
      subtitle="Mua sắm nhanh hơn, theo dõi đơn hàng và nhận ưu đãi riêng"
      footer={<>Đã có tài khoản? <Link to="/dang-nhap" className="text-brand-600 font-semibold hover:underline">Đăng nhập</Link></>}
    >
      {success ? (
        <div className="py-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-accent-500/15 text-accent-600 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <div className="font-semibold text-text text-lg">Đăng ký thành công!</div>
          <div className="text-sm text-muted mt-1">Đang chuyển về trang chủ...</div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {serverError && (
            <div className="rounded-md bg-danger/10 border border-danger/30 text-danger px-4 py-2.5 text-sm">
              {serverError}
            </div>
          )}

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Họ và tên</div>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input value={values.fullName} onChange={(e) => upd('fullName', e.target.value)} placeholder="Nguyễn Văn A" autoComplete="name" className={inputCls(errors.fullName)} />
            </div>
            {errors.fullName && <div className="text-xs text-danger mt-1">{errors.fullName}</div>}
          </label>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Username</div>
            <div className="relative">
              <AtSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input value={values.username} onChange={(e) => upd('username', e.target.value.toLowerCase())} placeholder="nguyenvana" autoComplete="username" className={inputCls(errors.username)} />
            </div>
            {errors.username && <div className="text-xs text-danger mt-1">{errors.username}</div>}
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">SĐT</div>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input value={values.phone} onChange={(e) => upd('phone', e.target.value)} placeholder="0912345678" inputMode="tel" autoComplete="tel" className={inputCls(errors.phone)} />
              </div>
              {errors.phone && <div className="text-xs text-danger mt-1">{errors.phone}</div>}
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Email</div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input type="email" value={values.email} onChange={(e) => upd('email', e.target.value)} placeholder="ban@email.com" autoComplete="email" className={inputCls(errors.email)} />
              </div>
              {errors.email && <div className="text-xs text-danger mt-1">{errors.email}</div>}
            </label>
          </div>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Mật khẩu</div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input type={showPass ? 'text' : 'password'} value={values.password} onChange={(e) => upd('password', e.target.value)} placeholder="Tối thiểu 6 ký tự" autoComplete="new-password" className={inputCls(errors.password) + ' pr-10'} />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {values.password && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(strength / 4) * 100}%` }} />
                </div>
                <span className="text-xs text-muted w-16 text-right">{strengthLabel}</span>
              </div>
            )}
            {errors.password && <div className="text-xs text-danger mt-1">{errors.password}</div>}
          </label>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Nhập lại mật khẩu</div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input type={showPass ? 'text' : 'password'} value={values.confirm} onChange={(e) => upd('confirm', e.target.value)} placeholder="Nhập lại mật khẩu" autoComplete="new-password" className={inputCls(errors.confirm)} />
            </div>
            {errors.confirm && <div className="text-xs text-danger mt-1">{errors.confirm}</div>}
          </label>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={values.agree as unknown as boolean}
              onChange={(e) => upd('agree', e.target.checked as unknown as true)}
              className="w-4 h-4 accent-brand-600 rounded mt-0.5 shrink-0"
            />
            <span className="text-sm text-text leading-snug">
              Tôi đồng ý với <Link to="/dieu-khoan" className="text-brand-600 hover:underline">Điều khoản sử dụng</Link> và <Link to="/chinh-sach-bao-mat" className="text-brand-600 hover:underline">Chính sách bảo mật</Link>
            </span>
          </label>
          {errors.agree && <div className="text-xs text-danger -mt-2">{errors.agree}</div>}

          <button
            type="submit"
            disabled={registerMut.isPending}
            className="w-full h-12 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition"
          >
            <UserPlus className="w-4 h-4" />
            {registerMut.isPending ? 'Đang tạo tài khoản...' : 'Tạo tài khoản'}
          </button>

          <SocialButtons />
        </form>
      )}
    </AuthCard>
  )
}
